import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Container,
  Divider,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Modal,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WorkflowBuilder from './WorkflowBuilder';
import restaurantWorkflow from './restaurant_workflow.json';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

// Sample language and voice options
const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'de-DE', label: 'German' },
  { value: 'it-IT', label: 'Italian' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'ko-KR', label: 'Korean' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
];

// Voice options for each language
const VOICE_OPTIONS = {
  'en-US': [
    { value: 'en-US-JennyNeural', label: 'Jenny (Female)' },
    { value: 'en-US-GuyNeural', label: 'Guy (Male)' },
    { value: 'en-US-DavisNeural', label: 'Davis (Male)' },
    { value: 'en-US-AriaNeural', label: 'Aria (Female)' },
  ],
  'en-GB': [
    { value: 'en-GB-SoniaNeural', label: 'Sonia (Female)' },
    { value: 'en-GB-RyanNeural', label: 'Ryan (Male)' },
  ],
  'es-ES': [
    { value: 'es-ES-ElviraNeural', label: 'Elvira (Female)' },
    { value: 'es-ES-AlvaroNeural', label: 'Alvaro (Male)' },
  ],
  'fr-FR': [
    { value: 'fr-FR-DeniseNeural', label: 'Denise (Female)' },
    { value: 'fr-FR-HenriNeural', label: 'Henri (Male)' },
  ],
  'de-DE': [
    { value: 'de-DE-KatjaNeural', label: 'Katja (Female)' },
    { value: 'de-DE-ConradNeural', label: 'Conrad (Male)' },
  ],
  'it-IT': [
    { value: 'it-IT-ElsaNeural', label: 'Elsa (Female)' },
    { value: 'it-IT-DiegoNeural', label: 'Diego (Male)' },
  ],
  'ja-JP': [
    { value: 'ja-JP-NanamiNeural', label: 'Nanami (Female)' },
    { value: 'ja-JP-KeitaNeural', label: 'Keita (Male)' },
  ],
  'ko-KR': [
    { value: 'ko-KR-SunHiNeural', label: 'SunHi (Female)' },
    { value: 'ko-KR-InJoonNeural', label: 'InJoon (Male)' },
  ],
  'pt-BR': [
    { value: 'pt-BR-FranciscaNeural', label: 'Francisca (Female)' },
    { value: 'pt-BR-AntonioNeural', label: 'Antonio (Male)' },
  ],
  'zh-CN': [
    { value: 'zh-CN-XiaoxiaoNeural', label: 'Xiaoxiao (Female)' },
    { value: 'zh-CN-YunxiNeural', label: 'Yunxi (Male)' },
  ],
};

/**
 * Restaurant Workflow Demo Component
 * Displays the restaurant workflow example in the workflow builder
 */
const RestaurantWorkflowDemo = () => {
  // State for the workflow
  const [workflow, setWorkflow] = useState(restaurantWorkflow);
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);
  const [jsonValue, setJsonValue] = useState(JSON.stringify(workflow, null, 2));
  const [jsonError, setJsonError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });
  
  // Fetch available agents for saving the workflow
  useEffect(() => {
    fetchAgents();
  }, []);
  
  const fetchAgents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/agents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };
  
  // Get voice options for a selected language
  const getVoiceOptions = (language) => {
    return VOICE_OPTIONS[language] || [];
  };
  
  // Handle changes to the workflow
  const handleWorkflowChange = (updatedWorkflow) => {
    setWorkflow(updatedWorkflow);
    setJsonValue(JSON.stringify(updatedWorkflow, null, 2));
  };
  
  // Update workflow from JSON editor
  const updateWorkflowFromJson = () => {
    try {
      const parsedWorkflow = JSON.parse(jsonValue);
      setWorkflow(parsedWorkflow);
      setJsonError(null);
      setAlertInfo({
        open: true,
        message: 'Workflow updated from JSON successfully',
        severity: 'success'
      });
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };
  
  // Handle changes to the JSON in the editor
  const handleJsonChange = (event) => {
    setJsonValue(event.target.value);
    try {
      JSON.parse(event.target.value);
      setJsonError(null);
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
    }
  };
  
  // Reset to the original workflow
  const handleReset = () => {
    setWorkflow(restaurantWorkflow);
    setJsonValue(JSON.stringify(restaurantWorkflow, null, 2));
  };

  // Save workflow to selected agent
  const saveToAgent = async () => {
    if (!selectedAgent) {
      setAlertInfo({
        open: true,
        message: 'Please select an agent first',
        severity: 'warning'
      });
      return;
    }
    
    try {
      // This endpoint would need to be implemented on your backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/agents/${selectedAgent}/workflow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ workflow })
      });
      
      if (response.ok) {
        setAlertInfo({
          open: true,
          message: 'Workflow saved to agent successfully',
          severity: 'success'
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save workflow');
      }
    } catch (error) {
      setAlertInfo({
        open: true,
        message: `Error saving workflow: ${error.message}`,
        severity: 'error'
      });
    }
  };
  
  const loadFromAgent = async () => {
    if (!selectedAgent) {
      setAlertInfo({
        open: true,
        message: 'Please select an agent first',
        severity: 'warning'
      });
      return;
    }
    
    try {
      // This endpoint would need to be implemented on your backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/agents/${selectedAgent}/workflow`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.workflow) {
          setWorkflow(data.workflow);
          setJsonValue(JSON.stringify(data.workflow, null, 2));
          setAlertInfo({
            open: true,
            message: 'Workflow loaded from agent successfully',
            severity: 'success'
          });
        } else {
          throw new Error('Agent has no workflow defined');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load workflow');
      }
    } catch (error) {
      setAlertInfo({
        open: true,
        message: `Error loading workflow: ${error.message}`,
        severity: 'error'
      });
    }
  };
  
  // Close alert messages
  const handleAlertClose = () => {
    setAlertInfo({ ...alertInfo, open: false });
  };
  
  // Helper to generate Python code from the workflow
  const generatePythonCode = () => {
    // This would generate Python code based on the workflow
    // (simplified for this demo)
    return `import logging
from dataclasses import dataclass, field
from typing import Annotated, Optional

import yaml
from dotenv import load_dotenv
from pydantic import Field

from livekit.agents import JobContext, WorkerOptions, cli, llm
from livekit.agents.llm import function_tool
from livekit.agents.voice import Agent, AgentSession, RunContext
from livekit.agents.voice.room_io import RoomInputOptions
from livekit.plugins import azure, openai, silero

logger = logging.getLogger("restaurant-example")
logger.setLevel(logging.INFO)

load_dotenv()

# Agent voice definitions
voices = {
    ${workflow.nodes.map(node => `"${node.name.toLowerCase()}": "${node.voice}"`).join(',\n    ')}
}

@dataclass
class UserData:
    customer_name: Optional[str] = None
    customer_phone: Optional[str] = None
    reservation_time: Optional[str] = None
    order: Optional[list[str]] = None
    customer_credit_card: Optional[str] = None
    customer_credit_card_expiry: Optional[str] = None
    customer_credit_card_cvv: Optional[str] = None
    expense: Optional[float] = None
    checked_out: Optional[bool] = None
    agents: dict[str, Agent] = field(default_factory=dict)
    prev_agent: Optional[Agent] = None

    def summarize(self) -> str:
        data = {
            "customer_name": self.customer_name or "unknown",
            "customer_phone": self.customer_phone or "unknown",
            "reservation_time": self.reservation_time or "unknown",
            "order": self.order or "unknown",
            "credit_card": {
                "number": self.customer_credit_card or "unknown",
                "expiry": self.customer_credit_card_expiry or "unknown",
                "cvv": self.customer_credit_card_cvv or "unknown",
            }
            if self.customer_credit_card
            else None,
            "expense": self.expense or "unknown",
            "checked_out": self.checked_out or False,
        }
        return yaml.dump(data)

RunContext_T = RunContext[UserData]

# Common functions for all agents
${workflow.nodes.reduce((acc, node) => {
  // Add all unique function tools across agents
  const tools = node.tools.filter(tool => 
    tool.name.startsWith('update_') || 
    (tool.name === 'confirm_expense') || 
    (tool.name === 'confirm_reservation') ||
    (tool.name === 'update_credit_card')
  );
  
  tools.forEach(tool => {
    // Add only unique tools
    if (!acc.includes(tool.name)) {
      acc.push(tool.name);
    }
  });
  
  return acc;
}, []).map(toolName => {
  // Find a tool with this name
  let foundTool = null;
  workflow.nodes.forEach(node => {
    const tool = node.tools.find(t => t.name === toolName);
    if (tool && !foundTool) foundTool = tool;
  });
  
  if (!foundTool) return '';
  
  // Generate Python code for the tool
  return `@function_tool()
async def ${foundTool.name}(
    ${foundTool.parameters.filter(p => p.name !== 'context').map(p => 
        `${p.name}: Annotated[${p.name === 'items' ? 'list[str]' : 
                                 p.name === 'expense' ? 'float' : 'str'}, Field(description="${p.description}")]`
    ).join(',\n    ')}${foundTool.parameters.filter(p => p.name !== 'context').length > 0 ? ',' : ''}
    context: RunContext_T,
) -> str:
    """${foundTool.description}"""
    userdata = context.userdata
    ${foundTool.name.startsWith('update_') ? 
        `userdata.${foundTool.name.replace('update_', '')} = ${foundTool.parameters.find(p => p.name !== 'context')?.name || 'value'}
    return f"The ${foundTool.name.replace('update_', '')} is updated to {${foundTool.parameters.find(p => p.name !== 'context')?.name || 'value'}}"` :
     foundTool.name === 'confirm_expense' ?
        `userdata.expense = expense
    return f"The expense is confirmed to be {expense}"` :
     foundTool.name === 'confirm_reservation' ?
        `if not userdata.customer_name or not userdata.customer_phone:
        return "Please provide your name and phone number first."

    if not userdata.reservation_time:
        return "Please provide reservation time first."

    return await self._transfer_to_agent("greeter", context)` :
     toolName === 'confirm_checkout' ?
        `if not userdata.expense:
        return "Please confirm the expense first."

    if (not userdata.customer_credit_card or 
        not userdata.customer_credit_card_expiry or 
        not userdata.customer_credit_card_cvv):
        return "Please provide the credit card information first."

    userdata.checked_out = True
    return await to_greeter(context)` :
     'return "Function executed successfully"'}
`;
}).join('\n\n')}

# Agent transition functions
${workflow.nodes.reduce((acc, node) => {
  // Add transition functions
  const transitionTools = node.tools.filter(tool => tool.name.startsWith('to_'));
  
  transitionTools.forEach(tool => {
    // Add only unique tools
    if (!acc.includes(tool.name)) {
      acc.push(tool.name);
    }
  });
  
  return acc;
}, []).map(toolName => {
  return `@function_tool()
async def ${toolName}(context: RunContext_T) -> Agent:
    """${workflow.nodes.find(node => 
       node.tools.find(t => t.name === toolName))?.tools.find(t => t.name === toolName)?.description || 
       `Called when transitioning to the ${toolName.replace('to_', '')} agent.`}"""
    curr_agent: BaseAgent = context.session.current_agent
    return await curr_agent._transfer_to_agent("${toolName.replace('to_', '')}", context)
`;
}).join('\n\n')}

class BaseAgent(Agent):
    async def on_enter(self) -> None:
        agent_name = self.__class__.__name__
        logger.info(f"entering task {agent_name}")

        userdata: UserData = self.session.userdata
        chat_ctx = self.chat_ctx.copy()

        # add the previous agent's chat history to the current agent
        if userdata.prev_agent:
            items_copy = self._truncate_chat_ctx(
                userdata.prev_agent.chat_ctx.items, keep_function_call=True
            )
            existing_ids = {item.id for item in chat_ctx.items}
            items_copy = [item for item in items_copy if item.id not in existing_ids]
            chat_ctx.items.extend(items_copy)

        # add an instructions including the user data as a system message
        chat_ctx.add_message(
            role="system",
            content=f"You are {agent_name} agent. Current user data is {userdata.summarize()}",
        )
        await self.update_chat_ctx(chat_ctx)
        self.session.generate_reply(tool_choice="none")

    async def _transfer_to_agent(self, name: str, context: RunContext_T) -> tuple[Agent, str]:
        userdata = context.userdata
        current_agent = context.session.current_agent
        next_agent = userdata.agents[name]
        userdata.prev_agent = current_agent

        return next_agent, f"Transferring to {name}."

    def _truncate_chat_ctx(
        self,
        items: list[llm.ChatItem],
        keep_last_n_messages: int = 6,
        keep_system_message: bool = False,
        keep_function_call: bool = False,
    ) -> list[llm.ChatItem]:
        """Truncate the chat context to keep the last n messages."""

        def _valid_item(item: llm.ChatItem) -> bool:
            if not keep_system_message and item.type == "message" and item.role == "system":
                return False
            if not keep_function_call and item.type in [
                "function_call",
                "function_call_output",
            ]:
                return False
            return True

        new_items: list[llm.ChatItem] = []
        for item in reversed(items):
            if _valid_item(item):
                new_items.append(item)
            if len(new_items) >= keep_last_n_messages:
                break
        new_items = new_items[::-1]

        # the truncated items should not start with function_call or function_call_output
        while new_items and new_items[0].type in ["function_call", "function_call_output"]:
            new_items.pop(0)

        return new_items

${workflow.nodes.map(node => {
  const nodeClassName = node.name;
  const tools = node.tools.map(t => t.name).join(', ');
  
  return `class ${nodeClassName}(BaseAgent):
    def __init__(self${nodeClassName === 'Greeter' || nodeClassName === 'Takeaway' || nodeClassName === 'Checkout' ? ', menu: str' : ''}) -> None:
        super().__init__(
            instructions="${node.instructions.replace(/"/g, '\\"')}",
            llm=openai.LLM(model="gpt-4o-mini", parallel_tool_calls=False),
            tts=azure.TTS(voice=voices["${node.name.toLowerCase()}"]),
        )
        ${nodeClassName === 'Greeter' || nodeClassName === 'Takeaway' || nodeClassName === 'Checkout' ? 'self.menu = menu' : ''}
`;
}).join('\n\n')}

async def entrypoint(ctx: JobContext):
    await ctx.connect()

    menu = "Pizza: $10, Salad: $5, Ice Cream: $3, Coffee: $2"
    userdata = UserData()
    userdata.agents.update(
        {
            ${workflow.nodes.map(node => 
              `"${node.name.toLowerCase()}": ${node.name}(${node.name === 'Greeter' || node.name === 'Takeaway' || node.name === 'Checkout' ? 'menu' : ''})`
            ).join(',\n            ')}
        }
    )
    agent = AgentSession[UserData](
        userdata=userdata,
        stt=azure.STT(),
        llm=openai.LLM(model="gpt-4o-mini"),
        tts=azure.TTS(),
        vad=silero.VAD.load(),
        max_tool_steps=5,
    )

    await agent.start(
        agent=userdata.agents["${workflow.nodes.find(n => n.is_initial).name.toLowerCase()}"],
        room=ctx.room,
        room_input_options=RoomInputOptions(),
    )

    await agent.say("${workflow.nodes.find(n => n.is_initial).initial_message}")


if __name__ == "__main__":
    cli.run_app(WorkerOptions(entrypoint_fnc=entrypoint))`;
  };

  // Add special restaurant layout function
  const applyRestaurantLayout = () => {
    if (workflow.nodes.length !== 4) {
      setAlertInfo({
        open: true,
        message: 'Restaurant layout requires exactly 4 nodes',
        severity: 'warning'
      });
      return;
    }
    
    // Get node positions
    const CENTER_X = 400;
    const CENTER_Y = 300;
    const HORIZONTAL_SPACING = 400;
    const VERTICAL_SPACING = 250;
    
    // Find the nodes
    let greeterNode = workflow.nodes.find(node => 
      node.name?.toLowerCase().includes('greeter') || 
      node.is_initial === true
    );
    
    let reservationNode = workflow.nodes.find(node => 
      node.name?.toLowerCase().includes('reservation')
    );
    
    let takeawayNode = workflow.nodes.find(node => 
      node.name?.toLowerCase().includes('takeaway') || 
      node.name?.toLowerCase().includes('order')
    );
    
    let checkoutNode = workflow.nodes.find(node => 
      node.name?.toLowerCase().includes('checkout') || 
      node.name?.toLowerCase().includes('payment')
    );
    
    // If we couldn't identify all nodes, assign based on index
    if (!greeterNode || !reservationNode || !takeawayNode || !checkoutNode) {
      const sortedNodes = [...workflow.nodes].sort((a, b) => {
        if (a.is_initial && !b.is_initial) return -1;
        if (!a.is_initial && b.is_initial) return 1;
        return a.id.localeCompare(b.id);
      });
      
      [greeterNode, reservationNode, takeawayNode, checkoutNode] = sortedNodes;
    }
    
    // Create updated nodes with new positions
    const updatedNodes = workflow.nodes.map(node => {
      if (node.id === greeterNode.id) {
        return { ...node, position: { x: CENTER_X, y: CENTER_Y - VERTICAL_SPACING } };
      } else if (node.id === reservationNode.id) {
        return { ...node, position: { x: CENTER_X - HORIZONTAL_SPACING, y: CENTER_Y } };
      } else if (node.id === takeawayNode.id) {
        return { ...node, position: { x: CENTER_X + HORIZONTAL_SPACING, y: CENTER_Y } };
      } else if (node.id === checkoutNode.id) {
        return { ...node, position: { x: CENTER_X, y: CENTER_Y + VERTICAL_SPACING } };
      }
      return node;
    });
    
    // Update workflow
    setWorkflow({
      ...workflow,
      nodes: updatedNodes
    });
    
    setAlertInfo({
      open: true,
      message: 'Restaurant diamond layout applied',
      severity: 'success'
    });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Restaurant Workflow Demo
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          This is a demonstration of a multi-agent restaurant workflow system built with the workflow builder. 
          The workflow consists of multiple agents handling different aspects of restaurant operations, including
          greeting customers, taking reservations, handling takeaway orders, and processing payments.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item>
            <Button variant="outlined" onClick={handleReset}>
              Reset Workflow
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={() => setJsonEditorOpen(true)}
            >
              Edit JSON
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="agent-select-label">Select Agent</InputLabel>
              <Select
                labelId="agent-select-label"
                value={selectedAgent}
                label="Select Agent"
                onChange={(e) => setSelectedAgent(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item>
            <Button 
              variant="contained" 
              color="primary"
              onClick={saveToAgent}
              disabled={!selectedAgent}
            >
              Save to Agent
            </Button>
          </Grid>
          
          <Grid item>
            <Button 
              variant="outlined" 
              color="primary"
              onClick={loadFromAgent}
              disabled={!selectedAgent}
            >
              Load from Agent
            </Button>
          </Grid>
          
          <Grid item>
            <Button 
              variant="outlined" 
              color="secondary"
              startIcon={<RestaurantMenuIcon />}
              onClick={applyRestaurantLayout}
              disabled={workflow.nodes.length !== 4}
              title="Apply restaurant diamond layout"
            >
              Restaurant Layout
            </Button>
          </Grid>
        </Grid>

        <WorkflowBuilder
          value={workflow}
          onChange={handleWorkflowChange}
          disabled={false}
          languageOptions={LANGUAGE_OPTIONS}
          getVoiceOptions={getVoiceOptions}
        />

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" gutterBottom>
          Generated Python Code
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          The following code would be generated from the workflow configuration:
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box component="pre" sx={{ 
            overflow: 'auto', 
            fontSize: '0.7rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}>
            {generatePythonCode()}
          </Box>
        </Paper>
        
        {/* JSON Editor Modal */}
        <Modal
          open={jsonEditorOpen}
          onClose={() => setJsonEditorOpen(false)}
          aria-labelledby="json-editor-modal"
          aria-describedby="edit-workflow-json"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            width: '80%',
            maxWidth: '900px',
            maxHeight: '80vh',
            borderRadius: 2,
            overflow: 'auto'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" component="h2">
                Edit Workflow JSON
              </Typography>
              <IconButton onClick={() => setJsonEditorOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <TextField
              fullWidth
              multiline
              variant="outlined"
              value={jsonValue}
              onChange={handleJsonChange}
              error={!!jsonError}
              helperText={jsonError}
              sx={{ 
                mb: 2,
                fontFamily: 'monospace',
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }
              }}
              minRows={15}
              maxRows={25}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setJsonEditorOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={() => {
                  updateWorkflowFromJson();
                  setJsonEditorOpen(false);
                }}
                disabled={!!jsonError}
              >
                Update Workflow
              </Button>
            </Box>
          </Box>
        </Modal>
        
        {/* Alert for notifications */}
        <Snackbar
          open={alertInfo.open}
          autoHideDuration={6000}
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleAlertClose} 
            severity={alertInfo.severity} 
            sx={{ width: '100%' }}
          >
            {alertInfo.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default RestaurantWorkflowDemo; 